// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Prompt Manager
// Route: /prompts
// apps/web/src/routes/prompts.tsx
//
// Owner/Admin-only page for viewing, editing, versioning, and testing
// prompts. The prompts are the product's intelligence — they live in SQL
// and can be evolved without a deployment.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  StatusBadge,
  LoadingShimmer,
  EmptyState,
} from '../components/engine/EnginePageShell';
import { useApiClient } from '@forgetrack/api-client';

export const Route = createFileRoute('/prompts')({
  component: PromptManagerPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface PromptDefinition {
  id: string;
  promptKey: string;
  displayName: string;
  description: string;
  category: string;
  ownedByPlatform: boolean;
  versionCount: number;
  activeVersion: number | null;
  activeModelName: string | null;
  totalInvocations: number;
  totalCostUSD: number;
  createdAt: string;
}

interface PipelineOverview {
  id: string;
  pipelineKey: string;
  displayName: string;
  description: string;
  isActive: boolean;
  steps: PipelineStep[];
}

interface PipelineStep {
  id: string;
  stepOrder: number;
  stepName: string;
  promptKey: string;
  promptDisplayName: string;
  requiredTier: string;
  runCondition: string | null;
  isDeepPass: boolean;
}

// ─── API Hooks ──────────────────────────────────────────────────────────────

function usePromptDefinitions() {
  const { createApi } = useApiClient();
  const [data, setData] = useState<PromptDefinition[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const api = await createApi();
      const res = await api.get('/api/v1/prompts/definitions');
      setData(res.data);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  }, [createApi]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
}

function usePipelines() {
  const { createApi } = useApiClient();
  const [data, setData] = useState<PipelineOverview[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const api = await createApi();
        const res = await api.get('/api/v1/prompts/pipelines');
        setData(res.data);
      } catch { /* swallow */ }
      finally { setLoading(false); }
    })();
  }, [createApi]);

  return { data, loading };
}

// ─── Page ───────────────────────────────────────────────────────────────────

// Route entry: gate between list view (exact /prompts) and the child route
// (prompts.$promptKey.tsx mounted through Outlet). Kept as a tiny dispatcher
// so hooks inside PromptLibraryView only run when the list actually renders —
// React error #300 (hooks-count mismatch) fires otherwise on nav away.
function PromptManagerPage() {
  const pathname = useRouterState({ select: s => s.location.pathname });
  if (pathname !== '/prompts') {
    return <Outlet />;
  }
  return <PromptLibraryView />;
}

function PromptLibraryView() {
  const { data: defs, loading: defsLoading } = usePromptDefinitions();
  const { data: pipelines, loading: pipelinesLoading } = usePipelines();
  const [activeTab, setActiveTab] = useState<'library' | 'pipelines'>('library');

  return (
    <EnginePageShell
      title="Prompt Manager"
      subtitle="View, version, and monitor the AI prompts that power ForgeTrack"
    >
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {(['library', 'pipelines'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={tabStyle(tab === activeTab)}
          >
            {tab === 'library' ? 'Prompt Library' : 'Pipelines'}
          </button>
        ))}
      </div>

      {activeTab === 'library' && (
        defsLoading ? <LoadingShimmer /> : (
          defs && defs.length > 0 ? (
            <PromptLibraryTable definitions={defs} />
          ) : (
            <EmptyState title="No prompt definitions found. Run the PromptLibrarySeeder." />
          )
        )
      )}

      {activeTab === 'pipelines' && (
        pipelinesLoading ? <LoadingShimmer /> : (
          pipelines && pipelines.length > 0 ? (
            pipelines.map(p => <PipelineCard key={p.id} pipeline={p} />)
          ) : (
            <EmptyState title="No pipelines configured." />
          )
        )
      )}
    </EnginePageShell>
  );
}

// ─── Prompt Library Table ───────────────────────────────────────────────────

function PromptLibraryTable({ definitions }: { definitions: PromptDefinition[] }) {
  // Group by category
  const categories = [...new Set(definitions.map(d => d.category))].sort();

  return (
    <div>
      {categories.map(cat => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <SectionLabel>{cat.toUpperCase()}</SectionLabel>
          <GlassPanel>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Prompt Key</th>
                  <th style={thStyle}>Display Name</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Version</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Model</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Calls</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {definitions.filter(d => d.category === cat).map(d => (
                  <tr key={d.id} style={rowStyle}>
                    <td style={tdStyle}>
                      <Link
                        to="/prompts/$promptKey"
                        params={{ promptKey: d.promptKey }}
                        style={{ fontFamily: F.mono, fontSize: 12, color: C.cyan, textDecoration: 'none' }}
                      >
                        {d.promptKey}
                      </Link>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12 }}>{d.displayName}</span>
                      <br />
                      <span style={{ fontSize: 10, color: C.dim }}>
                        {d.description.length > 80 ? d.description.slice(0, 80) + '...' : d.description}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {d.activeVersion ? (
                        <StatusBadge label={`v${d.activeVersion}`} color={C.neon} />
                      ) : (
                        <StatusBadge label="no active" color={C.coral} />
                      )}
                      <span style={{ fontSize: 10, color: C.dim, marginLeft: 4 }}>
                        ({d.versionCount} total)
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: 11, color: C.dim }}>
                      {d.activeModelName ?? '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: F.mono, fontSize: 11 }}>
                      {d.totalInvocations.toLocaleString()}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: F.mono, fontSize: 11 }}>
                      ${d.totalCostUSD.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassPanel>
        </div>
      ))}
    </div>
  );
}

// ─── Pipeline Card ──────────────────────────────────────────────────────────

function PipelineCard({ pipeline }: { pipeline: PipelineOverview }) {
  return (
    <GlassPanel style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontFamily: F.mono, fontWeight: 600, fontSize: 14, color: C.cyan }}>
          {pipeline.pipelineKey}
        </span>
        <StatusBadge
          label={pipeline.isActive ? 'Active' : 'Inactive'}
          color={pipeline.isActive ? C.neon : C.dim}
        />
        <span style={{ fontSize: 12, color: C.dim, marginLeft: 'auto' }}>
          {pipeline.steps.length} steps
        </span>
      </div>

      <p style={{ fontSize: 11, color: C.dim, margin: '0 0 12px 0', lineHeight: 1.4 }}>
        {pipeline.description}
      </p>

      {/* Step flow */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {pipeline.steps.map((step, i) => (
          <div key={step.id} style={stepRowStyle}>
            <span style={stepNumberStyle}>{step.stepOrder}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{step.stepName}</span>
              <span style={{ fontSize: 10, color: C.cyan, marginLeft: 8 }}>
                {step.promptKey}
              </span>
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <StatusBadge label={step.requiredTier} color={tierColor(step.requiredTier)} />
                {step.runCondition && (
                  <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
                    {formatCondition(step.runCondition)}
                  </span>
                )}
                {step.isDeepPass && (
                  <StatusBadge label="deep" color={C.violet} />
                )}
              </div>
            </div>
            {i < pipeline.steps.length - 1 && (
              <span style={{ color: C.dim, fontSize: 16, marginLeft: 8 }}>→</span>
            )}
          </div>
        ))}
      </div>
    </GlassPanel>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function tierColor(tier: string): string {
  switch (tier) {
    case 'Free': return C.neon;
    case 'Starter': return C.amber;
    case 'Pro': return C.violet;
    case 'Enterprise': return C.cyan;
    default: return C.dim;
  }
}

function formatCondition(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    return parsed.when ?? raw;
  } catch {
    return raw;
  }
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const tabStyle = (active: boolean): React.CSSProperties => ({
  fontFamily: F.mono,
  fontSize: 12,
  padding: '6px 16px',
  background: active ? `${C.cyan}20` : 'transparent',
  border: `1px solid ${active ? C.cyan : C.dim}40`,
  borderRadius: 6,
  color: active ? C.cyan : C.dim,
  cursor: 'pointer',
});

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: F.mono,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: C.dim,
  borderBottom: `1px solid ${C.dim}30`,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: `1px solid ${C.dim}15`,
  verticalAlign: 'top',
};

const rowStyle: React.CSSProperties = {
  transition: 'background 0.15s',
};

const stepRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 6,
  background: `${C.surface}`,
  marginBottom: 2,
};

const stepNumberStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: `${C.cyan}20`,
  color: C.cyan,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  fontFamily: F.mono,
  flexShrink: 0,
};
